import { useCallback, useEffect, useRef } from 'react';
import { VideoEventEmitter } from './types';

export type UseEndedProps = {
  emitter: VideoEventEmitter;
  getDurationMillis: () => number;
  onEnded?: () => void;
  videoRef: React.RefObject<HTMLVideoElement>;
};

/**
 * Since segments of media are being skipped, we have to stop the video at
 * duration - sumOfAllSkips.  This hook handles stopping the video at the right time
 * and firing the onEnded callback
 */
export default function useEnded({
  getDurationMillis,
  onEnded,
  videoRef,
}: UseEndedProps) {
  const isEnded = useRef(false);

  // this is meant to be wired into the modified timeupdate callback. it's
  // returned so that the parent can wire it up
  const maybeEnd = useCallback(
    (displayMillis: number) => {
      if (!isEnded.current && displayMillis >= getDurationMillis()) {
        videoRef.current.pause();
        isEnded.current = true;
        onEnded?.();
      }
    },
    [getDurationMillis, onEnded, videoRef],
  );

  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;

      const handlePlay = () => {
        isEnded.current = false;
      };

      video.addEventListener('play', handlePlay);

      return () => {
        video.removeEventListener('play', handlePlay);
      };
    }
    return undefined;
  }, [videoRef, isEnded]);

  return {
    handleTimeupdate: maybeEnd,
    isEnded: useCallback(() => isEnded.current, []),
  };
}
